import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Modal } from 'junto-ui';
import { motion } from 'framer-motion';
import { useStore } from '@store';
import Cookies from 'js-cookie';
import {
  ButtonWrapper,
  Content,
  IllustrationLabel,
  IllustrationTitle,
  IllustrationWrapper,
  NextButton,
  StartButton,
  ReponsiveWrapper,
  Wrapper,
  DotsContainer,
  Dot,
  CurrentDot,
} from './OnboardingModal.styles';
import IllustrationA from '../../../../assets/images/onboarding-illustration-a.svg';
import IllustrationB from '../../../../assets/images/onboarding-illustration-b.svg';
import IllustrationC from '../../../../assets/images/onboarding-illustration-c.svg';

const OnboardingList = [
  {
    img: <IllustrationA />,
    title: 'Sua garantia na mão em minutos',
    label: 'É mais fácil e rápido do que imagina. Cote e contrate online em poucos minutos.',
  },
  {
    img: <IllustrationB />,
    title: 'Garantia de segurança',
    label: 'A Junto Seguros é pioneira em Seguro Garantia, há mais de 25 anos no mercado.',
  },
  {
    img: <IllustrationC />,
    title: 'Dúvidas? Estamos aqui para ajudar ',
    label: 'Nossos especialistas estão prontos para ajudar, via Chat ou telefone.',
  },
];

const OnboardingModal = () => {
  const [mobileCurrentStep, setMobileCurrentStep] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [direction, setDirection] = useState('next');
  const sharedStore = useStore('sharedStore');

  const renderMobileIllustration = () => {
    const current = OnboardingList[mobileCurrentStep];

    const animation =
      direction === 'next'
        ? {
            initial: { opacity: 0, x: 600 },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: 600 },
          }
        : {
            initial: { opacity: 0, x: -600 },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: -600 },
          };

    let touchDistance = touchStart !== 0 && touchEnd !== 0 ? touchEnd - touchStart : 0;
    if (
      (touchDistance > 0 && mobileCurrentStep === 0) ||
      (touchDistance < 0 && mobileCurrentStep === 2)
    ) {
      touchDistance = 0;
    }

    return (
      <motion.div
        key={`container-anim-${mobileCurrentStep}`}
        initial={animation.initial}
        animate={animation.animate}
        exit={animation.exit}
        transition={{ duration: 0.6, type: 'tween' }}
        style={{ maxWidth: '312px', margin: 'auto' }}
      >
        <IllustrationWrapper touchDistance={touchDistance}>
          {current.img}
          <IllustrationTitle>{current.title}</IllustrationTitle>
          <IllustrationLabel>{current.label}</IllustrationLabel>
        </IllustrationWrapper>
      </motion.div>
    );
  };

  const renderMobileDots = () => {
    return (
      <DotsContainer>
        <CurrentDot currentStep={mobileCurrentStep} />
        <Dot />
        <Dot />
        <Dot />
      </DotsContainer>
    );
  };

  const onNext = () => {
    setMobileCurrentStep(s => s + 1);
    setDirection('next');
  };

  const onPrev = () => {
    setMobileCurrentStep(s => s - 1);
    setDirection('prev');
  };

  const handleTouchStart = touchStartEvent => {
    setTouchStart(touchStartEvent.targetTouches[0].clientX);
  };

  const handleTouchMove = touchMoveEvent => {
    setTouchEnd(touchMoveEvent.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchEnd !== 0 && touchStart - touchEnd > 75) {
      if (mobileCurrentStep < 2) {
        onNext();
      }
    }

    if (touchEnd !== 0 && touchStart - touchEnd < -75) {
      if (mobileCurrentStep > 0) {
        onPrev();
      }
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const onStart = () => {
    sharedStore.closeOnboardingModal();
    Cookies.set('junto-onboarding-complete', true, {
      expires: 90,
      domain: process.env.REACT_APP_COOKIE_DOMAIN,
    });
  };

  return (
    <Modal isOpen={sharedStore.displayOnboardingModal} mobileFullScreen hideCloseButton width="1024px">
      <Content>
        <Wrapper>
          {OnboardingList.map((item, index) => (
            <IllustrationWrapper key={`illustration-${index}`}>
              {item.img}
              <IllustrationTitle>{item.title}</IllustrationTitle>
              <IllustrationLabel>{item.label}</IllustrationLabel>
            </IllustrationWrapper>
          ))}
        </Wrapper>
        <ReponsiveWrapper
          onTouchStart={tse => handleTouchStart(tse)}
          onTouchMove={tme => handleTouchMove(tme)}
          onTouchEnd={() => handleTouchEnd()}
        >
          {renderMobileIllustration()}
          {renderMobileDots()}
        </ReponsiveWrapper>
        <ButtonWrapper>
          <StartButton
            onClick={onStart}
            currentStep={mobileCurrentStep}
            id="confirm-onboarding"
            data-testid="confirm-onboarding"
            variant="primary"
            size="big"
          >
            Começar
          </StartButton>
          {mobileCurrentStep !== 2 && (
            <NextButton
              onClick={onNext}
              id="next-onboarding"
              data-testid="next-onboarding"
              variant="primary"
              size="big"
            >
              Próximo
            </NextButton>
          )}
        </ButtonWrapper>
      </Content>
    </Modal>
  );
};

export default observer(OnboardingModal);
